import { useEffect } from "react";
import {
  useGetIdentity,
  useGetOne,
  useLocaleState,
  type Identifier,
} from "ra-core";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import type { Sale } from "../types";
import {
  ACCOUNT_LOCALES,
  type AccountLocale,
  localeToApply,
  useChangeAccountLocale,
} from "./accountLocale";

const useAccountSale = () => {
  const { identity } = useGetIdentity();
  return useGetOne<Sale>(
    "sales",
    { id: identity?.id as Identifier },
    { enabled: identity?.id != null },
  );
};

// Compte déjà synchronisé dans ce chargement de page. Pas une référence du composant : ra-core remonte toute
// l'application à chaque changement de langue, et la langue du compte encore en cache écraserait le choix.
let syncedSale: Identifier | null = null;

/** Applique une fois par compte, au chargement, la langue enregistrée dans le compte. */
export const AccountLocaleSync = () => {
  const { data: sale } = useAccountSale();
  const [locale, setLocale] = useLocaleState();
  useEffect(() => {
    if (!sale || syncedSale === sale.id) return;
    syncedSale = sale.id;
    const next = localeToApply(sale.locale, locale);
    if (next) setLocale(next);
  }, [sale, locale, setLocale]);
  return null;
};

/** Sélecteur « FR | EN », sans drapeaux : une langue n'est pas un pays. */
export const LocaleToggle = ({ className }: { className?: string }) => {
  const [locale] = useLocaleState();
  const change = useChangeAccountLocale();
  return (
    <ToggleGroup
      type="single"
      size="sm"
      variant="outline"
      value={locale}
      onValueChange={(value) => value && change(value as AccountLocale)}
      className={className}
    >
      {ACCOUNT_LOCALES.map((code) => (
        <ToggleGroupItem
          key={code}
          value={code}
          lang={code}
          aria-label={code === "fr" ? "Français" : "English"}
          className="px-3 text-xs font-semibold"
        >
          {code.toUpperCase()}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};
